import { useMemo } from 'react';
import { catalogHas, findCatalogItem, useCatalogItems } from './catalogItems';
import { usePartPhotos, type PartPhoto } from './partPhotos';
import { partGenKey, usePartsRun, type PartItem } from './partsRun';

// The parts the 3D phase works on: the manual's supplied parts that resolve to a catalog
// item AND that the Parts DB holds a photograph of, for the submodel on screen.
//
// One definition, read twice. The phase draws a chip per part and reconstructs the open
// one; the progress bar counts how many of them have a model. Those have to be the same
// list or the bar measures a denominator nobody can see — 12 of 13 done, with only twelve
// chips to click.
//
// A part with no photograph is left out rather than counted as unfinished: there is
// nothing to reconstruct from, no card to open, and no button to press. What is missing
// there is a Phase 2 photograph, and Phase 2's own bar is where that shows.

export type PhotographedPart = {
  // Submodel-aware, the same key Phase 2 generates under: the row's identity on screen.
  key: string;
  // The catalog row this part IS. Meshes and photographs both hang off it.
  itemId: number;
  part: PartItem;
  photos: PartPhoto[];
};

export function usePhotographedParts(docId: string, variant: string, epoch: number) {
  const { items } = usePartsRun(docId);
  const { items: catalog, loaded: catalogLoaded } = useCatalogItems();
  const { photos, loaded: photosLoaded } = usePartPhotos(docId, variant, epoch);

  const parts = useMemo<PhotographedPart[]>(() => {
    if (!catalogLoaded) return [];
    const byItem = new Map<number, PartPhoto[]>();
    for (const p of photos) {
      const list = byItem.get(p.itemId);
      if (list) list.push(p);
      else byItem.set(p.itemId, [p]);
    }
    const out: PhotographedPart[] = [];
    const seen = new Set<number>();
    for (const p of items) {
      // The extraction's own match first, but only if it still names a row — it ran
      // before this catalog did, and a deleted item leaves an id pointing at nothing.
      const itemId = catalogHas(catalog, p.catalogId)
        ? p.catalogId
        : findCatalogItem(catalog, { ...p, nameEn: p.nameEn || p.name });
      if (itemId == null || seen.has(itemId)) continue;
      const mine = byItem.get(itemId);
      if (!mine?.length) continue;
      seen.add(itemId);
      out.push({ key: partGenKey(p, variant) || String(itemId), itemId, part: p, photos: mine });
    }
    return out;
  }, [items, photos, catalog, catalogLoaded, variant]);

  return { parts, loaded: catalogLoaded && photosLoaded };
}

// EVERY PART OF THE MANUAL THAT HAS A ROW IN THE PARTS DB, photographed or not — what the assembly
// phase stands on. A part needs a photograph to be RECONSTRUCTED from one; it needs none to be drawn,
// and a model written rather than reconstructed (a wood button, cut from its hardware set) has none:
// read through the photographs, that button had "no 3D model" beside the model it has.
//
// The first part of the list to claim a row keeps it, as above.
export function useCataloguedParts(docId: string): { parts: { itemId: number; part: PartItem }[]; loaded: boolean } {
  const { items } = usePartsRun(docId);
  const { items: catalog, loaded } = useCatalogItems();
  const parts = useMemo(() => {
    if (!loaded) return [];
    const seen = new Set<number>();
    return items.flatMap((part) => {
      const itemId = catalogHas(catalog, part.catalogId)
        ? part.catalogId
        : findCatalogItem(catalog, { ...part, nameEn: part.nameEn || part.name });
      if (itemId == null || seen.has(itemId)) return [];
      seen.add(itemId);
      return [{ itemId, part }];
    });
  }, [items, catalog, loaded]);
  return { parts, loaded };
}
